/** Remove a uniform edge-connected background, not the logo's enclosed details. */
export function removeLogoBackgroundPixels(data: Uint8ClampedArray, width: number, height: number): void {
  if (width * height > 16_000_000) throw new Error("Logo is too large to process. Please upload a smaller image.");
  const edge: number[] = [];
  for (let x = 0; x < width; x++) edge.push(x, (height - 1) * width + x);
  for (let y = 1; y < height - 1; y++) edge.push(y * width, y * width + width - 1);
  const opaque = edge.filter(i => data[i * 4 + 3] > 240);
  // Existing transparency is preserved; there is no opaque rectangle to remove.
  if (opaque.length < edge.length * 0.5) return;
  const bins = new Map<string, number[]>();
  for (const i of opaque) {
    const key = [0, 1, 2].map(c => Math.round(data[i * 4 + c] / 16)).join(",");
    const group = bins.get(key) ?? [];
    group.push(i); bins.set(key, group);
  }
  const dominant = [...bins.values()].sort((a, b) => b.length - a.length)[0];
  if (!dominant || dominant.length < opaque.length * 0.5) {
    throw new Error("Could not identify a solid logo background. Please upload a transparent PNG.");
  }
  const bg = [0, 1, 2].map(c => Math.round(dominant.reduce((sum, i) => sum + data[i * 4 + c], 0) / dominant.length));
  const visited = new Uint8Array(width * height);
  const queue = new Uint32Array(width * height);
  let head = 0, tail = 0;
  const enqueue = (i: number) => {
    if (visited[i]) return;
    visited[i] = 1;
    const distance = Math.max(...bg.map((v, c) => Math.abs(v - data[i * 4 + c])));
    if (data[i * 4 + 3] === 0 || distance <= 64) queue[tail++] = i;
  };
  edge.forEach(enqueue);
  while (head < tail) {
    const i = queue[head++], p = i * 4;
    const distance = Math.max(...bg.map((v, c) => Math.abs(v - data[p + c])));
    const alpha = Math.max(0, Math.min(1, (distance - 24) / 40));
    // Unmatte soft edges so they don't leave a pale halo on the panel.
    if (alpha > 0 && alpha < 1) {
      for (let c = 0; c < 3; c++) data[p + c] = Math.max(0, Math.min(255, (data[p + c] - bg[c] * (1 - alpha)) / alpha));
    }
    data[p + 3] = Math.round(data[p + 3] * alpha);
    const x = i % width;
    if (x) enqueue(i - 1);
    if (x < width - 1) enqueue(i + 1);
    if (i >= width) enqueue(i - width);
    if (i < width * (height - 1)) enqueue(i + width);
  }
}

export function removeLogoCanvasBackground(canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D): void {
  const pixels = ctx.getImageData(0, 0, canvas.width, canvas.height);
  removeLogoBackgroundPixels(pixels.data, canvas.width, canvas.height);
  ctx.putImageData(pixels, 0, 0);
}

export function transparentLogoPreview(url: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    const timer = setTimeout(() => { image.src = ""; reject(new Error("Logo could not be loaded.")); }, 10_000);
    image.onload = () => {
      clearTimeout(timer);
      try {
        const canvas = document.createElement("canvas");
        canvas.width = image.naturalWidth; canvas.height = image.naturalHeight;
        const ctx = canvas.getContext("2d");
        if (!ctx) throw new Error("Logo processing is unavailable in this browser.");
        ctx.drawImage(image, 0, 0);
        removeLogoCanvasBackground(canvas, ctx);
        resolve(canvas.toDataURL("image/png"));
      } catch (error) { reject(error); }
    };
    image.onerror = () => { clearTimeout(timer); reject(new Error("Logo could not be loaded.")); };
    image.src = url;
  });
}
